<template lang="pug">
#family-reply-message-confirm
  // title
  .d-flex.flex-row.justify-content-center.mb-2
    .page-title 以下の内容で返信します。よろしければ送信ボタンを押してください。

  // message detail
  ReplyReceiveMessageDetail.mb-4(v-model="messageDetail" :planType="planType")

  // buttons
  .d-flex.flex-row.justify-content-center
    b-button.btn.send-btn.mr-2(@click="onSendMessage") 送信
    b-button.btn.back-btn(@click="onCancel") 戻る

  //- file upload error modal
  FileUploadErrorModal(v-model="showUploadErrorModal")

  // confirm reply modal
  OkModal(v-model="showCompleteReply" title="メッセージ送信に成功しました。" @ok="onCancel")
  // loading component
  Loading(:isBusy="isBusy", :message="loadingMessage")
  ProgressLoading(v-model="currentUploadCount", :max="uploadCount", :loading="progressLoading", message="ファイルアップロード")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore, mobileFamilyReplyReceiveMessageStore } from "@/store/store";
import { MobileFamilyReceiveMessageState } from "@/store/MobileFamilyReceiveMessage";
import { MobileFamilyReplyReceiveMessageState } from "@/store/MobileFamilyReplyReceiveMessage";
import { MobileUrl } from "@/commons/constants/MobileUrl";

import OpinionMessagesService, { OpinionMessagesPostModel, SendType } from "@/commons/services/OpinionMessages";
import ExistS3FilesService, { ExistS3FilesPostModel } from "@/commons/services/ExistS3Files";
import { S3LocationRequest, S3LocationResponse, S3LocationType, S3LocationMessage } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";
import S3LocationsService from "@/commons/services/S3Locations";
import { PlanType } from "@/commons/services/Plans";

import FileUploadErrorModal from "@/mobile/basics/FileUploadErrorModal.vue";
import Loading from "@/mobile/basics/Loading.vue";
import ProgressLoading from "@/mobile/basics/ProgressLoading.vue";
import ReplyReceiveMessageDetail, { ReplyReceiveMessageDetailModel } from "@/mobile/components/ReplyReceiveMessageDetail.vue";
import OkModal from "@/mobile/basics/OkModal.vue";

/**
 * ### 受信メッセージ返信（家族）コンポーネント
 */
@Component({
  components: {
    FileUploadErrorModal,
    Loading,
    ProgressLoading,
    ReplyReceiveMessageDetail,
    OkModal
  }
})
export default class FamilyReplyMessageConfirm extends Vue {
  /** 意見メッセージサービス */
  @Inject("OpinionMessagesService") opinionMessagesService!: OpinionMessagesService;
  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;
  /** S3ファイル存在確認サービス */
  @Inject("ExistS3FilesService") existS3FilesService!: ExistS3FilesService;

  /** 前画面で保持した情報 */
  @Getter("mobileFamilyReceiveMessageState") state!: MobileFamilyReceiveMessageState;
  /** 前画面で保持した入力情報 */
  @Getter("mobileFamilyReplyReceiveMessageState") inputState!: MobileFamilyReplyReceiveMessageState;

  /** プラン種別 */
  planType: PlanType = PlanType.UNKNOWN;

  /** メッセージ詳細 */
  messageDetail: ReplyReceiveMessageDetailModel | any = {};
  /** 添付ファイルを表示するか否か */
  showTempFile: boolean = false;

  /** 読み込み中モーダル表示可否 */
  isBusy: boolean = false;
  /** 読み込み中モーダルメッセージ */
  loadingMessage: string = "読込み中";
  /** 返信完了モーダル表示可否 */
  showCompleteReply: boolean = false;

  /** 現在のアップロード数 */
  currentUploadCount: number = 0;
  /** アップロード数最大値 */
  uploadCount: number = 0;
  /** プログレス付きローディング表示可否 */
  progressLoading: boolean = false;

  /** ファイルアップロードエラーモーダル表示可否 */
  showUploadErrorModal: boolean = false;

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    const planType: PlanType = userStore.facility.planType;
    if (PlanType.LIGHT <= planType) {
      this.showTempFile = true;
    } else {
      this.showTempFile = false;
    }
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // ブラウザバック対応。メッセージ送信後にブラウザバックされた場合にstoreに内容がないため表示する内容が無い
    // storeがリセットされていたら入力画面へ遷移させる
    if (this.inputState.exist == false) {
      this.$router.push(MobileUrl.FamilyReceiveMessageList);
    }
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    const messageDetail: ReplyReceiveMessageDetailModel = this.createMessageDetail(this.inputState);
    this.messageDetail = messageDetail;

    this.planType = userStore.facility.planType;

    this.isBusy = false;

    return;
  }

  /**
   * ### 送信メッセージ表示コンポーネントに渡す情報を作成する
   */
  public createMessageDetail(state: MobileFamilyReplyReceiveMessageState): ReplyReceiveMessageDetailModel {
    const messageDetail: ReplyReceiveMessageDetailModel = {
      text: state.text,
      files: state.files
    };
    return messageDetail;
  }

  /**
   * ### 送信ボタン押下イベント
   *
   * - ファイルをアップロードする
   * - 受信メッセージの返信をAPIにリクエストする
   * - storeの情報を初期化する
   * - 受信メッセージ詳細画面への遷移は完了モーダルのokボタン押下で発火するのでここでは送信するのみ
   */
  public async onSendMessage(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "送信中";

    // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
    // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
    let files: { fileUrls: string[]; fileNames: string[] } = { fileUrls: [], fileNames: [] };
    try {
      files = await this.uploadFiles();
    } catch (error) {
      this.isBusy = false;

      this.showUploadErrorModal = true;
      this.inputState.files = [];

      return;
    }

    // S3のアップロードは時間がかかるので他画面へ遷移されてしまうことがありえる
    // 画面遷移していたら処理は中断する
    if (this.$route.path != MobileUrl.FamilyReplyReceiveMessageConfirm + `/${this.$route.params.type}`) {
      return;
    }

    const requestBody: OpinionMessagesPostModel = this.createRequestBody(files);
    await this.opinionMessagesService.post(userStore, this.state.caseId, requestBody);

    this.isBusy = false;

    mobileFamilyReplyReceiveMessageStore.resetMobileFamilyReplyReceiveMessage();

    // 画面遷移していたら処理は中断する
    if (this.$route.path != MobileUrl.FamilyReplyReceiveMessageConfirm + `/${this.$route.params.type}`) {
      return;
    }

    this.showCompleteReply = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### ファイルをアップロードし、アップロードしたファイル情報を返却する
   *
   * - アップロードURLを作成する
   * - プログレスモーダルで進捗表示しつつ、ファイルをS3へアップロードする
   * - アップロードしたファイルのサムネイルの作成確認をする
   * - ファイル情報を作成して返却する
   */
  public async uploadFiles(): Promise<{ fileUrls: string[]; fileNames: string[] }> {
    let fileUrls: string[] = [];
    let fileNames: string[] = [];

    try {
      if (0 < this.inputState.files.length) {
        const urls: Array<S3LocationResponse> = await this.createUploadUrls(this.inputState.files);

        // プログレス付きモーダルを表示する
        // 進捗はファイルのアプロード状況を反映させるようにする
        this.currentUploadCount = 0;
        this.uploadCount = this.inputState.files.length;
        this.progressLoading = true;

        // アップロード
        for (let i = 0; i < urls.length; i++) {
          this.currentUploadCount = i;
          await this.s3UploadsService.uploadFile(urls[i], this.inputState.files[i]);

          // DBにはファイルではなく、添付ファイルのS3URLを保持するためURL配列を作成する
          const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
          const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;
          fileUrls.push(s3Url);
        }
        // アップロードしたファイルはサムネイル画像が作成されたり、movからmp4への変換が行われる
        // ファイルの存在確認をして変換されたか確認する
        await this.existFile(fileUrls);
      }

      for (const file of this.inputState.files) {
        fileNames.push(file.name);
      }
    } finally {
      this.progressLoading = false;
    }

    return { fileUrls: fileUrls, fileNames: fileNames };
  }

  /**
   * ### ファイルアップロード先のURLを発行する
   */
  public async createUploadUrls(files: Array<File>): Promise<Array<S3LocationResponse>> {
    let extensions: Array<string> = [];
    for (const file of files) {
      const _file: Array<string> = file.name.split("/");
      const _extensions: Array<string> = _file[_file.length - 1].split(".");
      extensions.push("." + _extensions[_extensions.length - 1]);
    }
    const params: S3LocationMessage = {
      userId: userStore.loginUser.userId
    };
    const requestBody: S3LocationRequest = {
      type: S3LocationType.RECEIVE_MESSAGES,
      count: files.length,
      extensions: extensions,
      params: params,
      acl: "public-read"
    };
    const url: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);

    return url;
  }

  /**
   * ### ファイルの存在確認を行う
   *
   * - アップロードしたファイルはサムネイル画像が作成されたり、movからmp4への変換が行われるので存在確認する
   */
  public async existFile(fileUrls: Array<string>): Promise<void> {
    // ファイルがアップロードされているかをチェックする
    const fileRequestBody: ExistS3FilesPostModel = { urls: fileUrls };
    await this.existS3FilesService.post(userStore, fileRequestBody);

    // サムネイルのファイルが存在しているかをチェックする
    let thumbnailUrls: Array<string> = [];
    for (const url of fileUrls) {
      // サムネイルのファイル名は<元ファイル名>_thumbnailになる
      const splitedUrl: string[] = url.split("/");
      const splitedFileName: string[] = splitedUrl[splitedUrl.length - 1].split(".");

      // mov、mp4以外の場合はサムネイルをメッセージ送信時に作成するのでスキップ
      if (splitedFileName[1] != "mov" && splitedFileName[1] != "mp4") {
        continue;
      }

      const thumbnailFileName: string = splitedFileName[0] + "_thumbnail.jpeg";
      let thunmbnailUrl: string = "";
      for (let i = 0; i < splitedUrl.length - 1; i++) {
        thunmbnailUrl += splitedUrl[i] + "/";
      }
      thunmbnailUrl += thumbnailFileName;
      thumbnailUrls.push(thunmbnailUrl);
    }

    if (0 < thumbnailUrls.length) {
      const thumbnailRequestBody: ExistS3FilesPostModel = { urls: thumbnailUrls };
      await this.existS3FilesService.post(userStore, thumbnailRequestBody);
    }

    return;
  }

  /**
   * ### 登録用パラメータを作成する
   */
  public createRequestBody(files: { fileUrls: string[]; fileNames: string[] }): OpinionMessagesPostModel {
    const requestBody: OpinionMessagesPostModel = {
      residentId: this.state.residentId,
      familyId: this.state.familyId,
      familyRelation: this.inputState.familyRelation,
      familyPosition: this.inputState.familyPosition,
      content: this.$stringUtil.convertYen(this.inputState.text),
      sender: "bot",
      senderType: SendType.FACILITY,
      receiver: this.inputState.receiver,
      fileUrls: files.fileUrls,
      fileNames: files.fileNames
    };
    return requestBody;
  }

  /**
   * ### キャンセルボタン押下イベント
   *
   * - 受信メッセージ詳細画面へ遷移する
   * - 受信メッセージ詳細画面から引き継いだ情報はそのまま返却する
   */
  public onCancel(): void {
    const type: string = this.$route.params.type;
    const page: string = this.$route.params.page;
    this.$router.push({ name: MobileUrl.FamilyReceiveMessageDetail, params: { type: type, page: page } });
    return;
  }
}
</script>

<style lang="scss" scopeed>
@import "../../../styles/mobile/_colors.scss";

#family-reply-message-confirm {
  #text {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  .preview-image,
  .preview-video {
    padding: 0.25rem;
    border: 1px solid $border-default;
    border-radius: 0.25rem;
  }
  .send-btn,
  .back-btn {
    min-width: 84px;
  }
}
</style>
